import { ChangeEvent, FormEvent } from 'react';
import { ICenterBase, IListItem } from '@nkr/models/index';
import { MicrositeListType, OrganType } from '@nkr/enums/index';
import { Button, FormControl, InputGroup, Well } from 'react-bootstrap';
import CoachSelector from './coach-selector';
import { useAppHookContext } from '@nkr/utils/rest-api';

interface IProps {
    coachContactId: number;
    center: number,
    status: number,
    type: number,
    startDate: string,
    endDate: string,
    search: string,
    campaign: string,
    listType: MicrositeListType,
    isAdmin: boolean,
    centerList: ICenterBase[],
    statusList: IListItem[],
    micrositeTypeList: IListItem[],
    canCreateCardBatch: boolean,
    centerId?: number;
    onChange: (property: string, value: any) => void,
    onSearch: () => void,
    onReset: () => void,
    onAdd: () => void,
    onCreateBatch: () => void,
    onExport: () => void,
}

const Search = (props: IProps) => {
    const { app: { site } } = useAppHookContext();

    const handleCenterChange = (e: ChangeEvent<FormControl & HTMLSelectElement>) => {
        let centerId = parseInt(e.currentTarget.value);
        props.onChange('center', centerId);
    };

    const handleStatusChange = (e: ChangeEvent<FormControl & HTMLSelectElement>) => {
        let statusId = parseInt(e.currentTarget.value);
        props.onChange('status', statusId);
    };

    const handleTypeChange = (e: ChangeEvent<FormControl & HTMLSelectElement>) => {
        let type = parseInt(e.currentTarget.value);
        props.onChange('type', type);
    }

    const handleSearchChange = (e: ChangeEvent<FormControl & HTMLInputElement>) => {
        props.onChange('search', e.currentTarget.value);
    };

    const handleCampaignChange = (e: ChangeEvent<FormControl & HTMLInputElement>) => {
        props.onChange('campaign', e.currentTarget.value);
    };

    const handleCoachChange = (e: ChangeEvent<FormControl & HTMLSelectElement & HTMLInputElement>) => {
        props.onChange('coachContactId', e.currentTarget.value);
    };

    const handleDateChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        let name = e.currentTarget.name;

        if (name == 'startDate') {
            props.onChange('startDate', e.currentTarget.value);
        }

        if (name == 'endDate') {
            props.onChange('endDate', e.currentTarget.value);
        }
    }

    const handleSearch = () => {
        props.onSearch();
    }

    const handleReset = () => {
        props.onReset();
    }

    return (
        <Well style={{ paddingTop: 9, paddingBottom: 9 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                    {props.isAdmin && (
                        <div style={{ marginRight: 10 }}>
                            <div>Filter by Center</div>
                            <div>
                                <FormControl
                                    componentClass='select'
                                    value={props.center}
                                    onChange={handleCenterChange}
                                >
                                    <option value={0}>- ALL -</option>
                                    <option value={-1}>- NO CENTER -</option>
                                    {props.centerList.map((item, index) =>
                                        <option key={'ctr-' + index} value={item.id}>{item.alias}</option>
                                    )}
                                </FormControl>
                            </div>
                        </div>
                    )}

                    {props.listType == MicrositeListType.All && (
                        <div style={{ marginRight: 10 }}>
                            <div>Filter by Status</div>
                            <div>
                                <FormControl
                                    componentClass='select'
                                    value={props.status}
                                    onChange={handleStatusChange}
                                >
                                    <option value={-1}>- ALL -</option>
                                    {props.statusList.map((item, index) =>
                                        <option key={'ctr-' + index} value={item.id}>{item.description}</option>
                                    )}
                                </FormControl>
                            </div>
                        </div>
                    )}

                    {site.organType != OrganType.Liver && (
                        <div style={{ marginRight: 10 }}>
                            <div>Organ Type</div>
                            <div>
                                <FormControl
                                    componentClass='select'
                                    value={props.type}
                                    onChange={handleTypeChange}
                                >
                                    <option value={-1}>- ALL -</option>
                                    {props.micrositeTypeList.map((item, index) =>
                                        <option key={'ctr-' + index} value={item.id}>{item.description}</option>
                                    )}
                                </FormControl>
                            </div>
                        </div>
                    )}

                    <div style={{ marginRight: 10 }}>
                        <div>Search by Name</div>
                        <div>
                            <FormControl
                                value={props.search}
                                onChange={handleSearchChange}
                            />
                        </div>
                    </div>

                    <div style={{ marginRight: 10 }}>
                        <div>Search by Campaign</div>
                        <div>
                            <FormControl
                                value={props.campaign}
                                onChange={handleCampaignChange}
                            />
                        </div>
                    </div>

                    <div style={{ marginRight: 10 }}>
                        <div>Coach</div>
                        <div>
                            <CoachSelector value={props.coachContactId} onChange={handleCoachChange} defaultText='- ALL -' allowNone={true} />
                        </div>
                    </div>

                    <div style={{ marginRight: 10 }}>
                        <div>Invite Start Date</div>
                        <div>
                            <FormControl
                                type='date'
                                name='startDate'
                                value={props.startDate}
                                onChange={handleDateChange}
                            />
                        </div>
                    </div>

                    <div style={{ marginRight: 10 }}>
                        <div>Invite End Date</div>
                        <div>
                            <FormControl
                                type='date'
                                name='endDate'
                                value={props.endDate}
                                onChange={handleDateChange}
                            />
                        </div>
                    </div>

                    <Button bsStyle='primary' onClick={handleSearch}>Search</Button>

                    <Button bsStyle='link' onClick={handleReset}>Reset Filters</Button>

                    {props.listType == MicrositeListType.PendingCards && props.canCreateCardBatch && (
                        <Button bsStyle='success' onClick={props.onCreateBatch}>Create Batch</Button>
                    )}&nbsp;

                    {(props.listType != MicrositeListType.PendingCards) && (
                        <Button onClick={props.onExport}>Export List</Button>
                    )}&nbsp;

                    <Button onClick={props.onAdd}>Add Microsite</Button>
                </div>
            </div>
        </Well>
    )
}

export default Search;
